import { useState, useRef, useEffect, useCallback } from "react";
import {
  Loader2,
  Send,
  GraduationCap,
  CheckCircle2,
  HelpCircle,
  ChevronRight,
  Mic,
  MicOff,
  PenLine,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MathContent } from "@/components/math/math-content";
import { AiNotice } from "@/components/discussion/ai-disclosure";
import { useSpeechInput } from "@/hooks/use-speech-input";
import type { Discussion } from "@/graphql/types";

type Props = {
  discussion: Discussion;
  sending: boolean;
  onSend: (message: string) => void;
  onResolve: (skipped: boolean) => void;
  /** Move on once the conversation is over. Absent when there is nowhere to go. */
  onNext?: () => void;
  nextLabel?: string;
  /**
   * Put the answer field back in front of the student, without ending the
   * conversation. Help only: on a chat opened by a wrong answer the field is
   * already answered and disabled, so there is nothing to go back to.
   */
  onBackToAnswer?: () => void;
};

/**
 * Matches DiscussionTutor::DONT_KNOW on the server, and the button on the task
 * itself. Deliberately not "Nežinau": at the moment a child feels worst, that
 * asks them to say something about themselves, and "I don't know" is a verdict
 * where "I want to understand" is an intention.
 */
const DONT_KNOW = "Noriu suprasti";

export function DiscussionPanel({
  discussion,
  sending,
  onSend,
  onResolve,
  onNext,
  nextLabel = "Kita užduotis",
  onBackToAnswer,
}: Props) {
  const [draft, setDraft] = useState("");
  // Dictation only fills the field — the student still presses Enter (or the
  // send button) themselves, so a stray sentence never gets sent by accident.
  const appendSpoken = useCallback((text: string) => {
    if (!text) return;
    setDraft((prev) => (prev ? `${prev.replace(/\s+$/, "")} ${text}` : text));
  }, []);
  const speech = useSpeechInput({ onFinal: appendSpoken });
  const panelRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const nextRef = useRef<HTMLButtonElement>(null);
  const isOpen = discussion.status === "OPEN";
  // While a second attempt is owed the student answers instead of chatting, so
  // the "I understand, continue" escape hatch is replaced by "Noriu suprasti".
  const awaitingRetry = isOpen && discussion.stage === "RETRY";
  // The student asked before answering. Nothing here is graded and the answer
  // field behind the panel is still theirs to fill in, so this conversation
  // leads back to the task rather than on to the next one.
  const isHelp = discussion.branch === "HELP";

  // Options the tutor offered with its latest question, shown as buttons. Only
  // the last message's: tapping an answer to a question two turns ago would
  // reply to the wrong thing. The server has already decided the question was
  // closed enough to deserve them.
  const last = discussion.messages[discussion.messages.length - 1];
  const suggestions =
    isOpen && !sending && last?.role === "ai" ? (last.suggestions ?? []) : [];
  // The fixed opening offers "Noriu suprasti" among its chips, and the tutor's
  // own turns do not — so the pill below the field is still the only way out of
  // those, and it is hidden only where it would stand next to its own twin. Read
  // off the message rather than off `suggestions`, which empties while a reply is
  // in flight: the pill would flash in under a chip row that is coming straight
  // back.
  // The theory's mini questions (TheoryErrorPath) can have sentence-long
  // options. A pill wrapping onto three lines reads as a broken button, so a row
  // with any long option is laid out as a list of full-width choices instead.
  const stackedChips = suggestions.some((option) => option.length > 28);
  const dontKnowOffered =
    last?.role === "ai" && (last.suggestions ?? []).includes(DONT_KNOW);

  // The chat appears while the student is looking at the task — below it after a
  // wrong answer, above the answer field when they asked for help — so bring it
  // into view either way, or the conversation is missed. Once only: later replies
  // shouldn't yank the page around.
  useEffect(() => {
    panelRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, []);

  // Once the answer is accepted — or the whole conversation is over — moving on
  // becomes the main action, so the student leaves the chat the same way they
  // were answering in it: with Enter.
  // Never in help mode: there is still an empty answer field on this task, and
  // sending the student to the next one would skip the very thing they were
  // getting ready to do.
  const showNext = !isHelp && !!onNext && (!isOpen || (discussion.solvedInChat && !awaitingRetry));

  // Keep the latest message in view, and put the keyboard wherever the student
  // is meant to act next.
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
    if (sending) return;
    // preventScroll so focusing doesn't fight the smooth scroll above.
    const t = setTimeout(() => {
      if (showNext) nextRef.current?.focus({ preventScroll: true });
      else if (isOpen) inputRef.current?.focus({ preventScroll: true });
    }, 80);
    return () => clearTimeout(t);
  }, [discussion.messages.length, sending, isOpen, showNext]);

  function submit(text?: string) {
    const value = (text ?? draft).trim();
    if (!value || sending) return;
    speech.stop();
    onSend(value);
    setDraft("");
  }

  return (
    <div
      ref={panelRef}
      className="mt-4 scroll-mt-20 rounded-lg border border-indigo-200 bg-indigo-50/60 dark:border-indigo-800 dark:bg-indigo-950/30"
    >
      <div className="flex items-center gap-2 border-b border-indigo-200 dark:border-indigo-800 px-4 py-2.5">
        <GraduationCap className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
        <span className="font-medium text-indigo-900 dark:text-indigo-100">
          {isHelp
            ? "Aiškinamės kartu"
            : awaitingRetry
              ? "Pabandyk dar kartą"
              : "Pasikalbėkime apie šią užduotį"}
        </span>
        {discussion.solvedInChat && (
          <span className="ml-auto inline-flex items-center gap-1 rounded-full bg-yellow-100 dark:bg-yellow-900/40 px-2 py-0.5 text-xs font-medium text-yellow-800 dark:text-yellow-200">
            <CheckCircle2 className="h-3.5 w-3.5" />
            {isHelp
              ? "Supratai!"
              : discussion.retryCorrect
                ? "Teisingai iš antro karto"
                : "Atsakei teisingai"}
          </span>
        )}
        {!isOpen && !discussion.solvedInChat && (
          <span className="ml-auto inline-flex items-center gap-1 text-sm text-green-700 dark:text-green-400">
            <CheckCircle2 className="h-4 w-4" />
            {discussion.status === "SKIPPED" ? "Praleista" : "Aptarta"}
          </span>
        )}
        {/* The moment a child works it out is rarely the moment the conversation
            ends, and the answer field is a chat's worth of scrolling away. This
            hands it back and leaves the chat exactly where it was — the exit at
            the foot of the panel is the one that closes it.

            Up here rather than beside that exit on purpose: two links a line
            apart, one ending the conversation and one not, is the three-similar-
            links problem the help ladder was built to remove. Different weight,
            different place, different job. */}
        {isOpen && isHelp && onBackToAnswer && (
          <button
            type="button"
            onClick={onBackToAnswer}
            className="ml-auto inline-flex flex-shrink-0 items-center gap-1.5 rounded-full border border-indigo-300 bg-white/70 px-3 py-1 text-sm font-medium text-indigo-800 hover:bg-white cursor-pointer dark:border-indigo-700 dark:bg-gray-900/50 dark:text-indigo-200 dark:hover:bg-gray-900"
          >
            <PenLine className="h-3.5 w-3.5" />
            Įrašyti atsakymą
          </button>
        )}
      </div>

      <AiNotice />

      <div ref={scrollRef} className="max-h-72 overflow-y-auto px-4 py-3 space-y-3">
        {discussion.messages.map((m) => (
          <div
            key={m.id}
            className={m.role === "student" ? "flex justify-end" : "flex justify-start"}
          >
            <div
              className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${
                m.role === "student"
                  ? "bg-indigo-600 text-white rounded-br-sm"
                  : "bg-white dark:bg-gray-800 text-foreground rounded-bl-sm border border-indigo-100 dark:border-gray-700"
              }`}
            >
              {/* The tutor draws the same figures the questions do, into a bubble
                  capped at 220px tall — the one place a figure is smallest and
                  most worth opening. */}
              <MathContent html={m.content} className="discussion-content inline" zoomableFigures />
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-bl-sm bg-white dark:bg-gray-800 border border-indigo-100 dark:border-gray-700 px-3 py-2">
              <Loader2 className="h-4 w-4 animate-spin text-indigo-500" />
            </div>
          </div>
        )}
      </div>

      {isOpen ? (
        <div className="border-t border-indigo-200 dark:border-indigo-800 p-3 space-y-2">
          {/* Answers to tap. They exist for the child who knows what they mean
              but stalls on spelling it, so they sit above the field and never
              replace it — anything else can still be typed or spoken. */}
          {suggestions.length > 0 && (
            <div className={stackedChips ? "flex flex-col gap-1.5" : "flex flex-wrap gap-2"}>
              {suggestions.map((option) => (
                <button
                  key={option}
                  type="button"
                  disabled={sending}
                  onClick={() => submit(option)}
                  className={`${stackedChips ? "rounded-lg text-left px-3 py-2" : "rounded-full px-3.5 py-1.5"} border border-indigo-300 bg-white text-sm font-medium text-indigo-800 hover:bg-indigo-100 disabled:opacity-50 cursor-pointer dark:border-indigo-700 dark:bg-gray-800 dark:text-indigo-200 dark:hover:bg-indigo-900/40`}
                >
                  <MathContent html={option} className="inline" />
                </button>
              ))}
            </div>
          )}
          <div className="flex items-center gap-2">
            <Input
              ref={inputRef}
              placeholder={
                speech.listening
                  ? "Klausausi... kalbėk 🎤"
                  : awaitingRetry
                    ? "Atsakymas ir kodėl taip manai..."
                    : "Parašyk arba padiktuok atsakymą..."
              }
              value={draft}
              disabled={sending}
              // Typing wins over the microphone: whatever is half-heard would
              // otherwise land in the middle of what's being typed.
              onChange={(e) => {
                if (speech.listening) speech.stop();
                setDraft(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  submit();
                }
              }}
            />
            {speech.supported && (
              <Button
                type="button"
                size="icon"
                variant={speech.listening ? "default" : "outline"}
                disabled={sending}
                aria-pressed={speech.listening}
                aria-label={speech.listening ? "Baigti diktuoti" : "Diktuoti atsakymą"}
                title={speech.listening ? "Baigti diktuoti" : "Diktuoti atsakymą"}
                onClick={() => {
                  speech.toggle();
                  inputRef.current?.focus({ preventScroll: true });
                }}
                className={speech.listening ? "animate-pulse bg-red-600 hover:bg-red-700" : undefined}
              >
                {speech.listening ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              </Button>
            )}
            <Button size="icon" disabled={sending || !draft.trim()} onClick={() => submit()}>
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
          </div>
          {/* Words the browser is still hearing out: they land in the field as
              soon as the phrase is finished. Nothing is ever sent on its own —
              the student presses Enter when the answer looks right. */}
          {speech.listening && (
            <p className="flex items-center gap-1.5 text-xs text-indigo-700 dark:text-indigo-300">
              <Mic className="h-3 w-3 shrink-0 animate-pulse" />
              <span className="truncate">
                {speech.interim || "Klausausi... kai baigsi, paspausk Enter arba ➤"}
              </span>
            </p>
          )}
          {speech.error && <p className="text-xs text-red-600 dark:text-red-400">{speech.error}</p>}
          {awaitingRetry ? (
            !dontKnowOffered && (
              <button
                type="button"
                disabled={sending}
                onClick={() => submit(DONT_KNOW)}
                className="inline-flex items-center gap-1.5 rounded-full border border-indigo-300 dark:border-indigo-700 px-3 py-1 text-sm text-indigo-700 dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/40 disabled:opacity-50 cursor-pointer"
              >
                <HelpCircle className="h-3.5 w-3.5" />
                {DONT_KNOW}
              </button>
            )
          ) : showNext ? (
            // The answer is in — one key closes the chat and moves on, while the
            // input above stays there for anyone who still wants to ask.
            <Button
              ref={nextRef}
              size="sm"
              onClick={() => {
                onResolve(false);
                onNext?.();
              }}
            >
              {nextLabel}
              <ChevronRight className="h-4 w-4" />
            </Button>
          ) : (
            // The tutor never closes the chat itself — it kept doing so in the
            // middle of a question — so this is the way out of every
            // conversation that didn't end with a right answer. It counts as
            // discussed, not skipped: the student sat through it.
            <button
              type="button"
              onClick={() => onResolve(false)}
              className="text-sm text-indigo-600 dark:text-indigo-400 hover:underline cursor-pointer"
            >
              {isHelp ? "Supratau — bandau atsakyti →" : "Suprantu, tęsti →"}
            </button>
          )}
        </div>
      ) : showNext ? (
        <div className="flex justify-end border-t border-indigo-200 dark:border-indigo-800 px-4 py-3">
          <Button ref={nextRef} onClick={onNext}>
            {nextLabel}
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div className="border-t border-indigo-200 dark:border-indigo-800 px-4 py-2 text-sm text-muted-foreground">
          {isHelp ? "Dabar įrašyk atsakymą pats." : "Gali tęsti į kitą užduotį."}
        </div>
      )}
    </div>
  );
}
